import awsdk from 'awsdk';
import {
  ChatReport,
  Consumer,
  Provider,
  VisitContext,
  FirstAvailableConfiguration,
  Visit,
  VisitCost,
  VisitSummary,
  ContextElements,
  VideoConsole,
  Appointment
} from 'lib/amwell/sdk/types';
import { VisitModalityType, Disposition } from 'lib/amwell/sdk/enums';
import { Transfer } from 'lib/amwell/sdk/types/transfer';
import Logger from 'services/Logger';
import { SpeedPass } from 'store/amwell/types';
import BaseService from './BaseService';
import AwsdkError from '../errors/AwsdkError';
import * as types from '../types/index';
import ValidationError from '../errors/ValidationError';

export default class VisitService extends BaseService {
  get telehealthVideoInstallUrl() {
    return this.sdk.visitService.telehealthVideoInstallUrl;
  }

  async getVisitContext(
    consumer: Consumer,
    providerOrFirstAvailableConfiguration: Provider | FirstAvailableConfiguration
  ): Promise<VisitContext> {
    const key = this.getVisitContext.name;

    try {
      return await this.sdk.visitService.getVisitContext(
        consumer,
        providerOrFirstAvailableConfiguration
      );
    } catch (err) {
      throw new AwsdkError(key, err);
    }
  }

  async getVisitContextForAppointment(appointment: Appointment): Promise<VisitContext> {
    const key = this.getVisitContextForAppointment.name;

    try {
      return await this.sdk.visitService.getVisitContextForAppointment(appointment);
    } catch (err) {
      throw new AwsdkError(key, err);
    }
  }

  async updateVisitContext(
    visitContext: VisitContext,
    updateVisitContextForm: Partial<types.UpdateVisitContextForm>
  ): Promise<VisitContext> {
    const key = this.updateVisitContext.name;

    const { otherTopic, callbackNumber, topics, triageQuestions } = updateVisitContextForm;

    try {
      if (
        !updateVisitContextForm ||
        (updateVisitContextForm && !Object.keys(updateVisitContextForm).length)
      ) {
        throw new awsdk.AWSDKError(awsdk.AWSDKErrorCode.privacyPolicyDisclaimerMissing);
      }

      const visitContextUpdate = visitContext;

      // Only support video modality
      visitContextUpdate.modalityType = VisitModalityType.VIDEO;

      if (typeof callbackNumber === 'string') {
        const cleanNumber = callbackNumber.replace(/\D/g, '');

        // Handle Validation Error for incorrect phone number format
        if (cleanNumber.length !== 10) {
          const error = new awsdk.AWSDKError(
            '', // errorCode
            '', // message
            'Invalid Format', // reason
            'Please enter valid phone number' // recoverySuggestion
          );
          error.fieldName = 'callbackNumber';
          error.fieldValue = cleanNumber;
          const validationError = new awsdk.AWSDKError(awsdk.AWSDKErrorCode.validationErrors);
          validationError.errors = [error];
          throw validationError;
        }

        visitContextUpdate.callbackNumber = cleanNumber;
      }

      if (typeof otherTopic === 'string') {
        visitContextUpdate.otherTopic = otherTopic || '';
      }

      visitContextUpdate.shareHealthSummary = true;

      if (topics && topics.length) {
        const visitContextTopics = visitContextUpdate.topics || [];
        visitContextTopics.forEach((_, idx) => {
          visitContextUpdate.topics[idx].selected = topics[idx].selected;
        });
      }

      if (triageQuestions && triageQuestions.length) {
        const visitContextQuestions = visitContextUpdate.triageQuestions || [];
        visitContextQuestions.forEach((_, idx) => {
          visitContextUpdate.triageQuestions[idx].answer = triageQuestions[idx]
            ? triageQuestions[idx].answer
            : '';
        });
      }

      return visitContextUpdate;
    } catch (err) {
      if (
        err instanceof awsdk.AWSDKError &&
        err.errorCode === awsdk.AWSDKErrorCode.validationErrors
      ) {
        throw new ValidationError(key, err.errors);
      }
      throw new AwsdkError(key, err);
    }
  }

  async createOrUpdateVisit(visitContext: VisitContext): Promise<Visit> {
    const key = this.createOrUpdateVisit.name;

    try {
      return await this.sdk.visitService.createOrUpdateVisit(visitContext);
    } catch (err) {
      throw new AwsdkError(key, err);
    }
  }

  async waitForVisitCostCalculationToFinish(visit: Visit): Promise<Visit> {
    const key = this.waitForVisitCostCalculationToFinish.name;

    try {
      return await this.sdk.visitService.waitForVisitCostCalculationToFinish(visit);
    } catch (err) {
      throw new AwsdkError(key, err);
    }
  }

  async applyCouponCode(visit: Visit, couponCode: string): Promise<VisitCost> {
    const key = this.applyCouponCode.name;

    try {
      return await this.sdk.visitService.applyCouponCode(visit, couponCode);
    } catch (err) {
      if (
        err instanceof awsdk.AWSDKError &&
        err.errorCode === awsdk.AWSDKErrorCode.validationErrors
      ) {
        throw new ValidationError(key, err.errors);
      }
      throw new AwsdkError(key, err);
    }
  }

  async findFirstAvailable(visit: Visit): Promise<Visit> {
    const key = this.findFirstAvailable.name;

    try {
      return await this.sdk.visitService.findFirstAvailable(visit);
    } catch (err) {
      throw new AwsdkError(key, err);
    }
  }

  async startVisit(visit: Visit): Promise<Visit> {
    const key = this.startVisit.name;

    try {
      return await this.sdk.visitService.startVisit(visit);
    } catch (err) {
      throw new AwsdkError(key, err);
    }
  }

  async waitForVisitToFinish(visit: Visit, cb?: (updatedVisit: Visit) => void): Promise<Visit> {
    const key = this.waitForVisitToFinish.name;

    try {
      return await this.sdk.visitService.waitForVisitToFinish(visit, cb);
    } catch (err) {
      throw new AwsdkError(key, err);
    }
  }

  async cancelVisit(visit: Visit): Promise<Visit> {
    const key = this.cancelVisit.name;

    try {
      return await this.sdk.visitService.cancelVisit(visit);
    } catch (err) {
      throw new AwsdkError(key, err);
    }
  }

  async endVisit(visit: Visit): Promise<Visit> {
    const key = this.endVisit.name;

    try {
      return await this.sdk.visitService.endVisit(visit);
    } catch (err) {
      throw new AwsdkError(key, err);
    }
  }

  async handleTransfer(visit: Visit): Promise<Transfer> {
    const key = this.handleTransfer.name;

    try {
      return await this.sdk.visitService.handleTransfer(visit);
    } catch (err) {
      throw new AwsdkError(key, err);
    }
  }

  async getVisitSummary(visit: Visit): Promise<VisitSummary> {
    const key = this.getVisitSummary.name;

    try {
      return await this.sdk.visitService.getVisitSummary(visit);
    } catch (err) {
      throw new AwsdkError(key, err);
    }
  }

  async sendVisitSummary(
    visitSummary: VisitSummary,
    hipaaNoticeAccepted: boolean,
    emailAddresses?: string[],
    faxNumbers?: string[]
  ): Promise<VisitSummary> {
    const key = this.sendVisitSummary.name;

    try {
      return await this.sdk.visitService.sendVisitSummary(
        visitSummary,
        hipaaNoticeAccepted,
        emailAddresses,
        faxNumbers
      );
    } catch (err) {
      if (
        err instanceof awsdk.AWSDKError &&
        err.errorCode === awsdk.AWSDKErrorCode.validationErrors
      ) {
        throw new ValidationError(key, err.errors);
      }
      throw new AwsdkError(key, err);
    }
  }

  async declineSuggestedTransfer(visit: Visit, dontAskAgain: boolean): Promise<Visit> {
    const key = this.declineSuggestedTransfer.name;

    try {
      return await this.sdk.visitService.declineSuggestedTransfer(visit, dontAskAgain);
    } catch (err) {
      if (
        err instanceof awsdk.AWSDKError &&
        err.errorCode === awsdk.AWSDKErrorCode.validationErrors
      ) {
        throw new ValidationError(key, err.errors);
      }
      throw new AwsdkError(key, err);
    }
  }

  async acceptSuggestedTransfer(visit: Visit): Promise<void> {
    const key = this.acceptSuggestedTransfer.name;

    try {
      await this.sdk.visitService.acceptSuggestedTransfer(visit);
    } catch (err) {
      if (
        err instanceof awsdk.AWSDKError &&
        err.errorCode === awsdk.AWSDKErrorCode.validationErrors
      ) {
        throw new ValidationError(key, err.errors);
      }
      throw new AwsdkError(key, err);
    }
  }

  async addFeedback(
    visitSummary: VisitSummary,
    question: string,
    answer: string
  ): Promise<boolean> {
    const key = this.addFeedback.name;

    try {
      return await this.sdk.visitService.addFeedback(visitSummary, question, answer);
    } catch (err) {
      throw new AwsdkError(key, err);
    }
  }

  async addRating(
    visitSummary: VisitSummary,
    providerRating: number,
    visitRating: number
  ): Promise<VisitSummary> {
    const key = this.addRating.name;

    try {
      return await this.sdk.visitService.addRating(visitSummary, providerRating, visitRating);
    } catch (err) {
      if (
        err instanceof awsdk.AWSDKError &&
        err.errorCode === awsdk.AWSDKErrorCode.validationErrors
      ) {
        throw new ValidationError(key, err.errors);
      }
      throw new AwsdkError(key, err);
    }
  }

  async setupWaitingRoomAlerts(
    visit: Visit,
    phoneNumber: string,
    providerReadyAlert?: boolean,
    movedUpInQueueAlert?: boolean
  ): Promise<boolean> {
    const key = this.setupWaitingRoomAlerts.name;
    try {
      return await this.sdk.visitService.setupWaitingRoomAlerts(
        visit,
        phoneNumber,
        providerReadyAlert,
        movedUpInQueueAlert
      );
    } catch (err) {
      if (
        err instanceof awsdk.AWSDKError &&
        err.errorCode === awsdk.AWSDKErrorCode.validationError
      ) {
        throw new ValidationError(key, err);
      }
      throw new AwsdkError(key, err);
    }
  }

  async addChatMessage(
    visit: Visit,
    message: string,
    lastPolledChatOrdinal: number
  ): Promise<ChatReport> {
    const key = this.addChatMessage.name;

    try {
      return await this.sdk.visitService.addChatMessage(visit, message, lastPolledChatOrdinal);
    } catch (err) {
      if (
        err instanceof awsdk.AWSDKError &&
        err.errorCode === awsdk.AWSDKErrorCode.validationError
      ) {
        throw new ValidationError(key, err);
      }
      throw new AwsdkError(key, err);
    }
  }

  async findActiveVisit(consumer: Consumer): Promise<Visit | null> {
    const key = this.findActiveVisit.name;

    try {
      return await this.sdk.visitService.findActiveVisit(consumer);
    } catch (err) {
      if (err.errorCode === awsdk.AWSDKErrorCode.visitNotFound) {
        return null;
      }
      throw new AwsdkError(key, err);
    }
  }

  async launchTelehealthVideo(visit: Visit): Promise<boolean> {
    const key = this.launchTelehealthVideo.name;

    try {
      return await this.sdk.visitService.launchTelehealthVideo(visit);
    } catch (err) {
      throw new AwsdkError(key, err);
    }
  }

  async processTelehealthVideoVisit(
    startedVisit: Visit,
    cb: (visit: Visit, type: types.VisitType, step: types.VisitStep) => void
  ): Promise<void> {
    const { VisitType, VisitStep } = types;
    const key = this.processTelehealthVideoVisit.name;
    const type = VisitType.TELEHEALTH_VIDEO;

    cb(startedVisit, type, VisitStep.INIT);

    try {
      const launched = await this.sdk.visitService.launchTelehealthVideo(startedVisit);
      if (!launched) {
        cb(startedVisit, type, VisitStep.SETUP);
      }
    } catch (err) {
      cb(startedVisit, type, VisitStep.SETUP);
    }

    try {
      if (startedVisit.disposition === Disposition.InProgress) {
        cb(startedVisit, type, VisitStep.STARTED);
      } else {
        let visit = await this.sdk.visitService.waitForTelehealthVideoToStart(startedVisit);

        cb(visit, type, VisitStep.PROCESSING);
        visit = await this.sdk.visitService.waitForProviderToJoinVisit(visit, updatedVisit => {
          cb(updatedVisit, type, VisitStep.PROCESSING);
        });

        if (visit.finished) {
          cb(visit, type, VisitStep.FINISHED);
        } else {
          cb(visit, type, VisitStep.STARTED);
        }
      }
    } catch (err) {
      throw new AwsdkError(key, err);
    }
  }

  async startTelehealthVideoVisit(
    processedVisit: Visit,
    cb: (visit: Visit, type: types.VisitType, step: types.VisitStep) => void
  ): Promise<void> {
    const { VisitType, VisitStep } = types;
    const key = this.startTelehealthVideoVisit.name;
    const type = VisitType.TELEHEALTH_VIDEO;

    try {
      let visit = await this.sdk.visitService.updateConnectionStatus(processedVisit);

      if (visit.finished || visit.canTransfer) {
        cb(visit, type, VisitStep.FINISHED);
      } else {
        cb(visit, type, VisitStep.STARTED);

        visit = await this.sdk.visitService.waitForVisitToFinish(visit, updatedVisit => {
          cb(updatedVisit, type, VisitStep.STARTED);
        });

        cb(visit, type, VisitStep.FINISHED);
      }
    } catch (err) {
      throw new AwsdkError(key, err);
    }
  }

  async processWebRTCVisit(
    startedVisit: Visit,
    cb: (visit: Visit, type: types.VisitType, step: types.VisitStep) => void
  ): Promise<void> {
    const { VisitType, VisitStep } = types;
    const key = this.processWebRTCVisit.name;
    const type = VisitType.WEB_RTC;

    try {
      cb(startedVisit, type, VisitStep.INIT);

      if (startedVisit.disposition === Disposition.InProgress) {
        cb(startedVisit, type, VisitStep.STARTED);
      } else {
        const visit = await this.sdk.visitService.waitForProviderToJoinVisit(
          startedVisit,
          updatedVisit => {
            cb(updatedVisit, type, VisitStep.PROCESSING);
          }
        );
        if (visit.finished) {
          cb(visit, type, VisitStep.FINISHED);
        } else {
          cb(visit, type, VisitStep.STARTED);
        }
      }
    } catch (err) {
      throw new AwsdkError(key, err);
    }
  }

  async startWebRTCVisit(
    processedVisit: Visit,
    webRTCContextElements: types.WebRTCContextElements,
    cb: (visit: Visit, type: types.VisitType, step: types.VisitStep) => void
  ): Promise<VideoConsole> {
    const { VisitType, VisitStep } = types;
    const key = this.startWebRTCVisit.name;
    const type = VisitType.WEB_RTC;

    try {
      const contextElements: ContextElements = {
        messages: webRTCContextElements.messages,
        consoleWrapperAttachPoints: webRTCContextElements.consoleWrapperAttachPoints,
        videoConsoleContainer: webRTCContextElements.videoConsoleContainer,
        successCallback: (visit: Visit) => cb(visit, type, VisitStep.FINISHED),
        updatedCallback: (visit: Visit) => {
          if (visit.canTransfer) {
            cb(visit, type, VisitStep.FINISHED);
          } else {
            cb(visit, type, VisitStep.STARTED);
          }
        },
        errorCallback: (visit: Visit, ...args) => {
          Logger.log(`startWebRTCVisit errorCallback `, visit, ...args);
          if (visit?.endReason?.length) {
            cb(visit, type, VisitStep.FINISHED);
          }
        }
      };

      const videoContext = this.sdk.visitService.createVideoContext(contextElements);
      return await this.sdk.visitService.startWebRTCVisit(processedVisit, videoContext);
    } catch (err) {
      throw new AwsdkError(key, err);
    }
  }

  async getSpeedPass(
    consumer: Consumer,
    providerOrFirstAvailableConfiguration: Provider | FirstAvailableConfiguration
  ): Promise<SpeedPass> {
    const key = this.getSpeedPass.name;
    try {
      return await this.sdk.visitService.getSpeedPass(
        consumer,
        providerOrFirstAvailableConfiguration
      );
    } catch (err) {
      throw new AwsdkError(key, err);
    }
  }
  async createVisitFromSpeedPass(speedPass: SpeedPass): Promise<Visit> {
    const key = this.createVisitFromSpeedPass.name;

    try {
      return await this.sdk.visitService.createVisitFromSpeedPass(speedPass);
    } catch (err) {
      throw new AwsdkError(key, err);
    }
  }
}
